import { useGameStore } from '@/store/gameStore'
import { useAudioContext } from '@/contexts/AudioContext'
import { fromMinor } from '@/utils/money'

interface ControlPanelProps {
  onCashOut: () => void
  onShop: () => void
  onDrop: () => void
  isDropping?: boolean
}

// Стиль для лейблов "Bet:" и "Win:" (Caesar Dressing 16px)
const labelFontStyle: React.CSSProperties = {
  fontFamily: "'Caesar Dressing', cursive",
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '91.8%',
  textAlign: 'center',
  color: '#F8DDCA',
}

// Стиль для цифр в Bet/Win (Big Shoulders Display — как в панели ставок)
const numberFontStyle: React.CSSProperties = {
  fontFamily: "'Big Shoulders Display', sans-serif",
  fontWeight: 800,
  fontSize: '18px',
  lineHeight: '100%',
  textAlign: 'center',
  color: '#F8DDCA',
}

// Общий стиль тёмной кнопки (#282E44)
const darkButtonStyle: React.CSSProperties = {
  background: '#282E44',
  boxShadow: 'inset 1px 2px 0px rgba(252, 255, 164, 0.25), inset 0px -5px 0px #1a2033',
  borderRadius: '2px',
}

// Общий стиль красной кнопки (Drop)
const redButtonStyle: React.CSSProperties = {
  background: '#DC3F48',
  boxShadow: 'inset 1px 2px 0px rgba(252, 255, 164, 0.25), inset 0px -5px 0px #282E44',
  borderRadius: '2px',
}

// Стиль текста кнопок (Caesar Dressing)
const btnTextStyle = (size: number): React.CSSProperties => ({
  fontFamily: "'Caesar Dressing', cursive",
  fontWeight: 400,
  fontSize: `${size}px`,
  lineHeight: '91.8%',
  textAlign: 'center',
  color: '#F8DDCA',
})

export default function ControlPanel({ onCashOut, onShop, onDrop, isDropping }: ControlPanelProps) {
  const { betAmount, currentStake } = useGameStore()
  const { playSfx } = useAudioContext()

  // Note: buttons are NOT disabled while waiting for the server's bet
  // confirmation (status !== 'playing' yet). Disabling there would cause a
  // brief opacity flash when ControlPanel mounts on BET IN. The parent
  // handlers (handleDrop / handleCashOut) silently no-op until the server
  // confirms the bet.
  const handleCashOut = () => { playSfx('click'); onCashOut() }
  const handleShop = () => { playSfx('click'); onShop() }
  const handleDrop = () => { playSfx('click'); onDrop() }

  return (
    <div 
      className="w-full"
      style={{
        background: '#F8DDCA',
        borderRadius: '3px',
        padding: '3px',
      }}
    >
      <div 
        className="relative"
        style={{ height: '118px' }}
      >
        {/* === ВЕРХНИЙ РЯД: BET и WIN лейблы (ширина = кнопки ниже) === */}
        
        {/* Bet label — ширина = Cash Out */}
        <div 
          className="absolute flex items-center justify-center gap-1"
          style={{
            ...darkButtonStyle,
            left: '1.5%',
            right: '67%',
            top: '0',
            height: '24px',
          }}
        >
          <span style={labelFontStyle}>Bet:</span>
          <span style={numberFontStyle}>{fromMinor(betAmount)}</span>
        </div>
        
        {/* Win label — ширина = Shop */}
        <div 
          className="absolute flex items-center justify-center gap-1"
          style={{
            ...darkButtonStyle,
            left: '34%',
            right: '36%',
            top: '0',
            height: '24px',
          }}
        >
          <span style={labelFontStyle}>Win:</span>
          <span style={numberFontStyle}>{fromMinor(currentStake)}</span>
        </div>

        {/* === НИЖНИЙ РЯД: Cash Out, Shop, Drop === */}
        
        {/* Cash Out (левая кнопка ~30%) */}
        <button
          onClick={handleCashOut}
          disabled={isDropping}
          className="absolute active:scale-95 transition-transform disabled:opacity-50 flex flex-col items-center justify-center"
          style={{
            ...darkButtonStyle,
            left: '1.5%',
            right: '67%',
            top: '26px',
            bottom: '0',
            gap: '2px',
          }}
        >
          <span style={btnTextStyle(22)}>Cash out</span>
          <span style={{ ...btnTextStyle(18), fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 800 }}>
            {fromMinor(currentStake)}
          </span>
        </button>
        
        {/* Shop (центральная кнопка ~30%) */}
        <button
          onClick={handleShop}
          disabled={isDropping}
          className="absolute active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center"
          style={{
            ...darkButtonStyle,
            left: '34%',
            right: '36%',
            top: '26px',
            bottom: '0',
          }}
        >
          <span style={btnTextStyle(37)}>Shop</span>
        </button>
        
        {/* Drop (правая красная кнопка ~30%, на всю высоту) */}
        <button
          onClick={handleDrop}
          disabled={isDropping}
          className="absolute active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center"
          style={{
            ...redButtonStyle,
            left: '65%',
            right: '1.5%',
            top: '0',
            bottom: '0',
          }}
        >
          <span style={btnTextStyle(46)}>Drop</span>
        </button>
      </div>
    </div>
  )
}
